import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { apiFetch, getAccessToken } from './api.js';
import { useAuth } from './auth-context.js';

interface OrgContextValue {
  /** The org being viewed. Null = platform mode (no org selected). */
  viewingOrgId: string | null;
  viewingOrgName: string | null;
  /** Whether the current user is in platform mode (admin without org selected) */
  isPlatformMode: boolean;
  /** Select an org to view (admin drill-in) */
  selectOrg: (orgId: string, orgName: string) => void;
  /** Deselect org (back to accounts list) */
  deselectOrg: () => void;
  /** The user's own org_id from JWT (null for admins) */
  userOrgId: string | null;
  /** The user's role */
  userRole: string | null;
  /** Whether user is admin/super_admin */
  isAdmin: boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [viewingOrgId, setViewingOrgId] = useState<string | null>(null);
  const [viewingOrgName, setViewingOrgName] = useState<string | null>(null);
  const [userOrgId, setUserOrgId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Initialize/re-initialize when auth state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setViewingOrgId(null);
      setViewingOrgName(null);
      setUserOrgId(null);
      setUserRole(null);
      return;
    }

    const token = getAccessToken();
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
      setUserRole(payload.role ?? null);
      setUserOrgId(payload.org_id ?? null);

      // Tenant users auto-select their org
      if (payload.org_id && payload.role !== 'super_admin' && payload.role !== 'admin') {
        setViewingOrgId(payload.org_id);
        apiFetch<{ name: string }>(`/orgs/${payload.org_id}`)
          .then(data => setViewingOrgName(data.name))
          .catch(() => {});
      }
    } catch { /* */ }
  }, [isAuthenticated]);

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isPlatformMode = isAdmin && !viewingOrgId;

  const selectOrg = useCallback((orgId: string, orgName: string) => {
    setViewingOrgId(orgId);
    setViewingOrgName(orgName);
  }, []);

  const deselectOrg = useCallback(() => {
    setViewingOrgId(null);
    setViewingOrgName(null);
  }, []);

  return (
    <OrgContext.Provider value={{
      viewingOrgId, viewingOrgName, isPlatformMode,
      selectOrg, deselectOrg,
      userOrgId, userRole, isAdmin,
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
}

/**
 * Fetch with org_id scoping. For admins viewing an account, appends ?org_id=.
 * For tenant users, the API uses the JWT org_id automatically.
 */
export async function orgFetch<T>(path: string, orgId: string | null, options: RequestInit = {}): Promise<T> {
  const separator = path.includes('?') ? '&' : '?';
  const scopedPath = orgId ? `${path}${separator}org_id=${orgId}` : path;
  return apiFetch<T>(scopedPath, options);
}
